if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.bindActiveMediaNavigation();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        if (event.detail.currentElement) this.setActiveMediaState(event.detail.currentElement);

        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      bindActiveMediaNavigation() {
        const previousButton = this.elements.viewer?.querySelector('button[name="previous"]');
        const nextButton = this.elements.viewer?.querySelector('button[name="next"]');

        [
          [previousButton, -1],
          [nextButton, 1],
        ].forEach(([button, direction]) => {
          if (!button) return;

          button.addEventListener(
            'click',
            (event) => {
              const handled = this.slideFromActiveMedia(direction);
              if (!handled) return;

              event.preventDefault();
              event.stopImmediatePropagation();
            },
            true
          );
        });
      }

      slideFromActiveMedia(direction) {
        const visibleMedia = this.getVisibleMediaItems();
        if (visibleMedia.length < 2) return false;

        const activeMedia = this.getActiveMediaItem(visibleMedia);
        const activeIndex = visibleMedia.indexOf(activeMedia);
        const nextIndex = Math.max(0, Math.min(visibleMedia.length - 1, activeIndex + direction));
        const nextMedia = visibleMedia[nextIndex];
        if (!nextMedia || nextMedia === activeMedia) return false;

        this.setActiveMedia(nextMedia.dataset.mediaId, false);
        return true;
      }

      getVisibleMediaItems() {
        return Array.from(this.elements.viewer?.slider?.querySelectorAll('[data-media-id]') || []).filter((item) => {
          const style = window.getComputedStyle(item);
          return style.display !== 'none' && style.visibility !== 'hidden' && item.clientWidth > 0;
        });
      }

      getActiveMediaItem(visibleMedia) {
        const activeMedia = visibleMedia.find((item) => item.classList.contains('is-active'));
        if (activeMedia) return activeMedia;

        return visibleMedia.reduce((closest, item) => {
          if (!closest) return item;
          const currentDistance = Math.abs(item.offsetLeft - this.elements.viewer.slider.scrollLeft);
          const closestDistance = Math.abs(closest.offsetLeft - this.elements.viewer.slider.scrollLeft);
          return currentDistance < closestDistance ? item : closest;
        }, null);
      }

      setActiveMediaState(activeMedia) {
        if (!activeMedia) return;

        activeMedia.classList.add('is-active');
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          if (element !== activeMedia) element.classList.remove('is-active');
        });
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.setActiveMediaState(activeMedia);

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.syncViewerToActiveMedia(activeMedia);
        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            this.syncViewerToActiveMedia(activeMedia);
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      syncViewerToActiveMedia(activeMedia) {
        if (!this.elements.viewer?.slider || !activeMedia) return;

        this.elements.viewer.resetPages?.();
        if (this.elements.viewer.setSlidePosition) {
          this.elements.viewer.setSlidePosition(activeMedia.offsetLeft);
        } else {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        this.elements.viewer.update?.();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        this.elements.thumbnails
          .querySelectorAll('.thumbnail-list__item.is-active')
          .forEach((element) => element.classList.remove('is-active'));
        thumbnail.classList.add('is-active');
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        window.requestAnimationFrame(() => this.scrollActiveThumbnailIntoView(thumbnail));
      }

      scrollActiveThumbnailIntoView(thumbnail) {
        if (!thumbnail?.isConnected) return;

        const slider = this.elements.thumbnails.slider;
        if (!slider) return;

        const isVertical = slider.scrollHeight > slider.clientHeight + 1;
        if (isVertical) {
          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
          const visibleTop = slider.scrollTop;
          const visibleBottom = visibleTop + slider.clientHeight;

          if (thumbnailTop >= visibleTop && thumbnailBottom <= visibleBottom) return;

          slider.scrollTo({
            top: Math.max(0, thumbnailTop - (slider.clientHeight - thumbnail.offsetHeight) / 2),
            behavior: 'smooth',
          });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        slider.scrollTo({
          left: Math.max(0, thumbnail.offsetLeft - (slider.clientWidth - thumbnail.offsetWidth) / 2),
          behavior: 'smooth',
        });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
